import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, forkJoin } from 'rxjs';

import * as _ from 'lodash';

import { CommentsService } from 'shared/comments/comments.service';
import { BrandService } from 'shared/brand/brand.service';
import { UserService } from 'shared/user/user.service';
import { PreferencesService } from 'shared/preferences/preferences.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import {first} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'dashboard-comments',
  templateUrl: 'dashboard-comments.component.html',
  styleUrls: ['dashboard-comments.component.scss'],
})
export class DashboardCommentsComponent implements OnInit, OnDestroy {
  comments: Array<any> = [];
  perPage: number = 100;
  unreadCount: number = 0;
  currentUserId: any;
  commentsFetched: boolean = false;

  private brandSubscription: Subscription;
  private timezone: string;

  constructor(
    private brandService: BrandService,
    private commentsService: CommentsService,
    private userService: UserService,
    private preferencesService: PreferencesService,
    private permissionsService: PermissionsService,
  ) {}

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get status() {
    return this.unreadCount > 0 ? `${this.unreadCount} unread` : 'No unread';
  }

  get brandId(): string {
    return this.brandService.getBrand();
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    this.getComments();
    this.userService.getUser()
     .pipe(
         first()
     )
    .subscribe((user: any) => {
      this.currentUserId = _.get(user, 'id');
    });
    this.brandSubscription = this.brandService.brandChange
    .subscribe((brand: any) => {
      this.getComments();
    });

    this.preferencesService.getPreferences()
    .subscribe((preferences: any) => {
      this.timezone = preferences.timezone;
    });
  }

  ngOnDestroy() {
    if (this.brandSubscription) { this.brandSubscription.unsubscribe(); }
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  // checks if we can refer user to certain pages
  public isCommentAllowedToRef(comment: any) {
    switch (comment.subject_type_id) {
      case 1:
        // product-details
        if (!_.has(comment, 'subject')) { return false; }

        // if it's not published and user have no permission
        if (comment.subject.status !== 8 && !this.hasPermission(['admin', 'catalog-edit'])) {
          return false;
        }
        return this.hasPermission(['admin', 'catalog-view', 'catalog-edit', 'repository']);
      case 2:
        // digital-asset
        if (!_.has(comment, 'subject')) { return false; }

        return this.hasPermission(['admin', 'dam-view', 'dam-edit', 'repository']);
      case 3:
      case 4:
      case 5:
      case 6:
        // global-brand-information
        return this.hasPermission(['admin', 'catalog-edit']);
      default:
        return true;
    }
  }

  public hasPermission(allow: Array<string>|string, requireAll: boolean= false) {
    return this.permissionsService.hasPermission(allow, requireAll);
  }

  public getCommentReference(comment: any): string {
    switch (comment.subject_type_id) {
      case 1:
        if (_.has(comment, 'subject.itemId')) {
          return `/product-details-v2/${comment.subject.itemId}/item-details`;
        } else {
          return '/dashboard';
        }
      case 2:
        if (_.has(comment, 'subject._id')) {
          return `/digital-asset/${comment.subject._id}/details`;
        } else {
          return '/dashboard';
        }
      case 3:
        return '/global-brand-information/product-groups';
      case 4:
        return '/global-brand-information/marketing';
      case 5:
        return '/global-brand-information/core';
      case 6:
        return '/global-brand-information/price-sheets';
      default:
        return '/dashboard';
    }
  }

  public getCommentReferenceTitle(comment: any): string {
    switch (comment.subject_type_id) {
      case 1:
        if (!_.has(comment, 'subject')) { return `Archived ${comment.subject_id}`; }

        let subTitle = '';
        if (_.has(comment, 'subject.partNumber')) {
            subTitle = `(${comment.subject.partNumber})`;
        }
        return `${_.get(comment.subject, 'descriptions.DES.EN', '')} ${subTitle}`;
      case 2:
        return `${_.get(comment.subject, 'filename', 'Digital Asset')}`;
      case 3:
        return 'Product Groups';
      case 4:
        return 'Market Copy';
      case 5:
        return 'Core';
      case 6:
        return 'Price Sheets';
      default:
        return '';
    }
  }

  public isUnread(comment: any): boolean {
    return !_.includes(comment.read_by, this.currentUserId);
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private getTooltipHtml(comment: any) {
    // TODO: add last_updated
    return `
      <ul>
        <li>${comment.user.first_name} ${comment.user.last_name}</li>
        <li>${comment.user.title ? comment.user.title : ''}</li>
      </ul>
    `;
  }

  private getComments(): void {
    forkJoin([
      this.commentsService.fetchFeedComments(this.brandId, this.perPage),
      this.commentsService.getTotalUnreadCount(this.brandId),
    ])
        .pipe(
            first()
        )
    .subscribe((result: Array<any>) => {
      this.comments = Array.isArray(_.get(result, '[0].data')) ? result[0].data : [];
      this.unreadCount = _.get(result, '[1].count', 0);
      this.commentsFetched = true;
    });
  }
}
